import { type ValidationError, ErrorTypes } from '@wix/form-validator';
import { type DateFieldErrorSuffix } from '../date-field/date-field-utils';
import { deserializeDate } from '../date-picker/helpers';
import { type DateTimeFieldError } from '../date-picker/types';
import { SupportedDateParts } from '../../utils/date-format-parts';

export const parseDateTimeValue = (value?: string): [string, string] => {
  if (!value) {
    return ['', ''];
  }

  const separator = value.indexOf('T') > -1 ? 'T' : ' ';
  const [date, time] = value.split(separator);

  return [date, time];
};

export const formatDateTimeValue = (
  date?: string | null,
  time?: string | null,
) => `${date ?? ''}T${time ?? ''}`;

export const isIncompleteTime = (time?: string) => {
  if (!time) {
    return true;
  }
  const [hours, minutes] = time.split(':');
  return !hours || !minutes;
};

export enum DateTimeFieldErrorSuffix {
  YEAR_MONTH_TIME = 'year-month-time',
  YEAR_DAY_TIME = 'year-day-time',
  MONTH_DAY_TIME = 'month-day-time',
  YEAR_TIME = 'year-time',
  MONTH_TIME = 'month-time',
  DAY_TIME = 'day-time',
  TIME = 'time',
}

export interface DateTimeFieldValidationError
  extends Omit<ValidationError, 'errorType' | 'params'> {
  errorType: ErrorTypes.INCOMPLETE_DATE_ERROR;
  params: {
    suffix: DateTimeFieldErrorSuffix | DateFieldErrorSuffix;
  };
}

export const getDateTimePartsErrors = (
  hasError: boolean,
  value?: string,
): DateTimeFieldError => {
  const [date, time] = parseDateTimeValue(value);
  const { YEAR, MONTH, DAY } = deserializeDate(date);
  const invalidParts = [
    YEAR?.length !== 4,
    !MONTH,
    !DAY,
    isIncompleteTime(time),
  ];
  const allValidParts = !invalidParts.some(Boolean);

  return {
    [SupportedDateParts.YEAR]: hasError && (allValidParts || invalidParts[0]),
    [SupportedDateParts.MONTH]: hasError && (allValidParts || invalidParts[1]),
    [SupportedDateParts.DAY]: hasError && (allValidParts || invalidParts[2]),
    TIME: hasError && (allValidParts || invalidParts[3]),
  };
};

const isDateTimeFieldValidationError = (
  error?: DateTimeFieldValidationError | ValidationError,
): error is DateTimeFieldValidationError => {
  return error?.errorType === ErrorTypes.INCOMPLETE_DATE_ERROR;
};

export const getDateTimeValidationErrorSuffix = (
  error?: DateTimeFieldValidationError | ValidationError,
) => (isDateTimeFieldValidationError(error) ? error.params.suffix : undefined);
